import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { useNostrPublish } from './useNostrPublish';
import { useToast } from './useToast';

/**
 * Fetches and manages the current user's NIP-02 follow list (kind 3).
 * Provides helpers to follow and unfollow a pubkey.
 */
export function useFollowList() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutateAsync: publish } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query the user's kind-3 follow list
  const query = useQuery({
    queryKey: ['nostr', 'follow-list', user?.pubkey ?? ''],
    queryFn: async ({ signal }) => {
      if (!user) return [];
      const [event] = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) },
      );
      // Extract all pubkeys from `p` tags
      return event?.tags.filter(([name]) => name === 'p').map(([, pubkey]) => pubkey) ?? [];
    },
    enabled: !!user,
    staleTime: 60 * 1000,
  });

  const followedPubkeys: string[] = query.data ?? [];

  function isFollowing(pubkey: string) {
    return followedPubkeys.includes(pubkey);
  }

  async function follow(pubkey: string) {
    if (!user) {
      toast({ title: 'Not logged in', description: 'You must be logged in to follow someone.', variant: 'destructive' });
      return;
    }
    if (isFollowing(pubkey)) return;

    // Fetch the latest follow-list tags (to preserve relay hints, petnames, etc.)
    const [existing] = await nostr.query(
      [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
      { signal: AbortSignal.timeout(5000) },
    );
    const existingTags = existing?.tags ?? [];
    const newTag: string[] = ['p', pubkey];

    try {
      await publish({
        kind: 3,
        content: existing?.content ?? '',
        tags: [...existingTags, newTag],
      });
      await queryClient.invalidateQueries({ queryKey: ['nostr', 'follow-list', user.pubkey] });
      toast({ title: 'Followed', description: 'You are now following this user.' });
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'Failed to follow user. Please try again.', variant: 'destructive' });
    }
  }

  async function unfollow(pubkey: string) {
    if (!user) {
      toast({ title: 'Not logged in', description: 'You must be logged in to unfollow someone.', variant: 'destructive' });
      return;
    }
    if (!isFollowing(pubkey)) return;

    const [existing] = await nostr.query(
      [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
      { signal: AbortSignal.timeout(5000) },
    );
    const existingTags = existing?.tags ?? [];
    const filteredTags = existingTags.filter(([name, value]) => !(name === 'p' && value === pubkey));

    try {
      await publish({
        kind: 3,
        content: existing?.content ?? '',
        tags: filteredTags,
      });
      await queryClient.invalidateQueries({ queryKey: ['nostr', 'follow-list', user.pubkey] });
      toast({ title: 'Unfollowed', description: 'You have unfollowed this user.' });
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'Failed to unfollow user. Please try again.', variant: 'destructive' });
    }
  }

  return {
    followedPubkeys,
    isFollowing,
    follow,
    unfollow,
    isLoading: query.isLoading,
  };
}
